<template>
    <div class="kb-files">
        <div class="header">
            <div class="title">{{ $t("chatbotv2.files") }}</div>
            <div class="actions">
                <SupsisButton primary @click="selectFile">
                    <i class="material-icons">add</i>
                    <span>{{ $t("chatbotv2.addFile") }}</span>
                </SupsisButton>
            </div>
        </div>
        <div v-if="files.length">
            <div>
                <div>
                    <ve-pagination
                        :total="total"
                        :page-index="page"
                        :page-size="pageSize"
                        :page-size-option="[1, 10, 15, 20, 50, 100]"
                        @on-page-number-change="(val) => (page = val)"
                        @on-page-size-change="(val) => (pageSize = val)"
                    />
                </div>
                <div class="files">
                    <div class="file" v-for="kb in files" :key="kb._id">
                        <div class="file-body">
                            <div class="file-icon">
                                <i class="material-icons">{{ getFileIconType(kb) }}</i>
                            </div>
                            <div class="file-name">{{ getFileName(kb) }}</div>
                        </div>
                        <div class="file-actions">
                            <a-tooltip placement="top" title="Delete">
                                <SupsisButton small ghost :disabled="loading" @click="() => deleteFile(kb)">
                                    <i class="material-icons">delete</i>
                                </SupsisButton>
                            </a-tooltip>
                            <a-tooltip placement="top" title="Open file">
                                <SupsisButton small ghost :disabled="loading" @click="() => openFile(kb)">
                                    <i class="material-icons">open_in_new</i>
                                </SupsisButton>
                            </a-tooltip>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <ComingSoon
            v-else
            :action-text="$t('chatbotv2.addFile')"
            :no-data-text="$t('chatbotv2.noFileAdded')"
            :addContent="selectFile"
            :disabled="false"
        />
        <div class="uploading-banner" v-if="uploading">
            <a-progress
                type="circle"
                :stroke-color="{
                    '0%': '#fff',
                    '100%': '#108ee9',
                }"
                :percent="uploadProgress"
            />
            <div class="upload-info">
                <span class="upload-status">{{ $t("chatbotv2.fileUploading") }}</span>
                <span class="upload-details" v-if="totalFiles > 1">
                    {{ completedFiles }} / {{ totalFiles }} dosya tamamlandı
                </span>
                <span class="upload-warning">
                    <i class="material-icons">warning</i>
                    Sayfadan ayrılmayınız, yükleme devam ediyor...
                </span>
            </div>
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import ComingSoon from "../ComingSoon.vue";
import { getExtensionFromFileName } from "../../../../../utils/utils";
import { uploadAsset } from "../../../../../api/assetUpload";
import mimeTypes from "../../../../../components/util/mimeTypes";
import axios from "axios";
import { mapState } from "vuex";
import log2 from "../../../../../utils/log2";
import { CHATBOT_API } from "../../../../../api/chatbotv2";
import { sendSocketMessage, socketMessageStream } from "../../../../../utils/socketStream";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";

const log = log2("KBFiles");
export default Vue.extend({
    name: "KBFiles",
    components: { SupsisButton, ComingSoon },
    props: {
        storyId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            total: 0,
            page: 1,
            pageSize: 10,
            files: [],
            uploading: false,
            uploadProgress: 0,
            loading: false,
            uploadQueue: [],
            currentUploadIndex: 0,
            totalFiles: 0,
            completedFiles: 0,
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
        }),
    },
    methods: {
        selectFile() {
            let fileEl = document.createElement("input");
            const supportedFileTypes = [
                "application/csv",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                "text/html",
                "application/json",
                "text/markdown",
                "text/x-markdown",
                "application/pdf",
                "application/vnd.openxmlformats-officedocument.presentationml.presentation",
                "text/x-tex",
                "text/plain",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                "application/xml",
                ".md", 
            ];
            fileEl.setAttribute("type", "file");
            fileEl.setAttribute("accept", supportedFileTypes.join(","));
            fileEl.setAttribute("multiple", "true");

            fileEl.onchange = async () => {
                let files = Array.from(fileEl.files);
                if (files.length > 0) {
                    this.uploadQueue = files;
                    this.totalFiles = files.length;
                    this.completedFiles = 0;
                    this.currentUploadIndex = 0;
                    this.uploading = true;
                    this.uploadProgress = 0;
                    
                    this.preventPageClose();
                    
                    await this.uploadFilesSequentially();
                }
            };
            fileEl.click();
        },
        async uploadFilesSequentially() {
            for (let i = 0; i < this.uploadQueue.length; i++) {
                this.currentUploadIndex = i;
                const file = this.uploadQueue[i];
                
                try {
                    await this.uploadSingleFile(file);
                    this.completedFiles++;
                } catch (error) {
                    console.error(`Dosya yükleme hatası: ${file.name}`, error);
                    this.$message.error({
                        content: `${file.name} dosyası yüklenemedi`,
                        duration: 3,
                    });
                }
                
                this.uploadProgress = Math.round((this.completedFiles / this.totalFiles) * 100);
            }
            
            this.uploading = false;
            this.allowPageClose();
            
            this.$message.success({
                content: `${this.completedFiles} dosya başarıyla yüklendi`,
                duration: 3,
            });
        },
        async uploadSingleFile(file) {
            return new Promise(async (resolve, reject) => {
                if (file.size > 10 * 1024 * 1024) {
                    this.$message.error({
                        content: `${file.name} - ${this.$t("message.assetOversize")}`,
                        duration: 3,
                    });
                    reject(new Error("File too large"));
                    return;
                }

                try {
                    const fileType = getExtensionFromFileName(file.name);
                    let path = `${this.site._id}/chatbot-knowledge-base/`;

                    let { data } = await uploadAsset({
                        path,
                        mime: file.type,
                        ext: mimeTypes[file.type] || fileType || "txt",
                        name: file.name,
                        size: file.size,
                        type: "chatbot"
                    });

                    const response = await axios({
                        method: data.method,
                        url: data.url,
                        data: file,
                        headers: {
                            "Content-Type": file.type,
                        },
                    });

                    const kb = await CHATBOT_API.KNOWLEDGE_BASE.createKnowledgeBase(
                        this.storyId,
                        "file",
                        undefined,
                        data.asset._id,
                        undefined,
                        undefined
                    ).then((res) => res.data);

                    this.files.push(kb);
                    this.$emit("updated", this.files);

                    sendSocketMessage("chatbot/v2 kb-file-added", {
                        siteId: this.site._id,
                        storyId: this.storyId,
                        ids: [kb._id],
                        isAssistantAPI: !!this.$store.state.CB.stories.find((story) => story._id === this.storyId)?.ai?.assistantId,
                    });

                    resolve(response);
                } catch (error) {
                    reject(error);
                }
            });
        },
        preventPageClose() {
            this.beforeUnloadHandler = (event) => {
                const message = 'Dosya yükleme işlemi devam ediyor. Sayfadan ayrılmayınız!';
                event.preventDefault();
                event.returnValue = message;
                return message;
            };
            window.addEventListener('beforeunload', this.beforeUnloadHandler);
        },
        allowPageClose() {
            if (this.beforeUnloadHandler) {
                window.removeEventListener('beforeunload', this.beforeUnloadHandler);
                this.beforeUnloadHandler = null;
            }
        },
        openFile(kb) {
            const asset = kb.file.asset;
            const url = asset.v2Format && asset.cdnUrl
            ? asset.cdnUrl
            :`https://static.supsis.live/${asset.path}${asset._id}.${asset.ext}`;
            window.open(url, "_blank");
        },
        deleteFile(item) {
            this.loading = true;
            CHATBOT_API.KNOWLEDGE_BASE.deleteMultiKnowledgeBase(this.storyId, [item._id])
                .then((res) => {
                    this.$message.success({
                        content: this.$t("chatbotv2.fileDeleted"),
                        duration: 2,
                        key: "delete",
                    });
                    this.files = this.files.filter((f) => f._id !== item._id);
                    this.$emit("updated", this.files);

                    sendSocketMessage("chatbot/v2 kb-file-removed", {
                        siteId: this.site._id,
                        storyId: this.storyId,
                        ids: [item._id],
                        isAssistantAPI: !!this.$store.state.CB.stories.find((story) => story._id === this.storyId)?.ai?.assistantId,
                    });
                })
                .catch((err) => {
                    this.$message.error({
                        content: this.$t("chatbotv2.fileDeletedFailed"),
                        duration: 2,
                        key: "delete",
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        listenSocket() {
            this.sub = socketMessageStream.subscribe(({ cmd, data }) => {
                if (cmd === "retrain_completed") {
                    const { success, isDelete } = data;
                    if (success) {
                        const message = isDelete
                            ? `Dosyalar silindi ve ve eğitim başlatıldı. Bu işlem bir kaç dakika sürebilir.`
                            : `Dosya yüklendi ve eğitim başlatıldı. Bu işlem bir kaç dakika sürebilir.`;
                        this.$message.success({
                            content: message,
                            duration: 3,
                            key: "retrain",
                        });
                    } else {
                        const message = isDelete
                            ? `Dosyalar silindi faka eğitim başlatılamadı. Lütfen tekrar deneyiniz.`
                            : `Dosya yüklendi fakat eğitim başlatılamadı. Lütfen tekrar deneyiniz.`;
                        this.$message.error({
                            content: message,
                            duration: 3,
                            key: "retrain",
                        });
                    }
                }
            });
        },
        fetch(page, pageSize) {
            this.loading = true;
            CHATBOT_API.KNOWLEDGE_BASE.listKnowledgeBase(this.storyId, "file", page, pageSize).then((res) => {
                this.files = res.data.rows;
                this.total = res.data.total;
                this.loading = false;
            });
        },
        getFileName(item) {
            const asset = item.file.asset;
            const defaultName = `${asset._id}.${asset.ext}`;

            return asset.name || defaultName;
        },
        getFileIconType(item) {
            const asset = item.file.asset;
            const ext = asset.ext;
            if (ext === "pdf") {
                return "picture_as_pdf";
            } else if (ext === "docx" || ext === "doc") {
                return "description";
            } else if (ext === "xlsx" || ext === "xls") {
                return "table_chart";
            } else if (ext === "pptx" || ext === "ppt") {
                return "slideshow";
            } else if (ext === "txt") {
                return "text_snippet";
            } else if (ext === "csv") {
                return "table_rows";
            } else if (ext === "json") {
                return "code";
            } else if (ext === "xml") {
                return "code";
            } else if (ext === "html") {
                return "code";
            } else {
                return "insert_drive_file";
            }
        },
    },
    mounted() {
        this.listenSocket();
        this.fetch();
    },
    beforeDestroy() {
        this.sub.unsubscribe();
        this.allowPageClose();
    },
    watch: {
        page() {
            this.fetch(this.page, this.pageSize);
        },
        pageSize() {
            this.fetch(this.page, this.pageSize);
        },
    },
});
</script>

<style lang="scss">
.kb-files {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;

    .uploading-banner {
        .ant-progress-text {
            color: white !important;
        }

        .ant-progress {
            width: 35px !important;
            height: 35px !important;

            .ant-progress-inner {
                width: 35px !important;
                height: 35px !important;
            }

            svg {
                width: 35px !important;
                height: 35px !important;
            }
        }
    }
}
</style>
<style scoped lang="scss">
.kb-files {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px 0px;
        width: 100%;

        .title {
            color: #22272d;
            display: flex;
            font-size: 22px;
            font-weight: 600;
            line-height: 28px;
            align-items: center;
        }
    }

    .uploading-banner {
        position: absolute;
        top: 92px;
        left: 0;

        width: calc(100% - 400px);
        height: auto;
        min-height: 60px;
        background: rgba(240, 244, 247, 0.99);
        display: -webkit-box;
        display: -ms-flexbox;
        display: flex;
        -ms-flex-direction: column;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        margin-left: 373px;
        color: white;
        background: #22272d;
        gap: 15px;
        padding: 15px;
        z-index: 1000;

        .upload-info {
            display: flex;
            flex-direction: column;
            gap: 8px;
            text-align: center;

            .upload-status {
                font-size: 16px;
                font-weight: 600;
            }

            .upload-details {
                font-size: 14px;
                color: #e0e0e0;
            }

            .upload-warning {
                font-size: 13px;
                color: #ffeb3b;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 5px;

                .material-icons {
                    font-size: 16px;
                }
            }
        }
    }

    .files {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
        align-items: flex-start;

        .file {
            display: flex;
            flex-direction: column;
            width: 200px;
            height: 200px;
            background: var(--color-bg-neutral-default);
            border-radius: 4px;
            position: relative;
            border: 2px solid var(--color-bg-neutral-default);
            cursor: pointer;

            &:hover {
                border: 2px solid #22272d;
            }

            .file-body {
                display: flex;
                flex-direction: column;
                width: 100%;
                gap: 5px;
                height: 100%;
                padding: 20px 10px 10px;

                .file-icon {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    font-size: 50px;

                    .material-icons {
                        font-size: 50px;
                    }
                }

                .file-name {
                    height: 40px;
                    font-size: 14px;
                    font-weight: 500;
                    text-align: center;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
            }

            .file-actions {
                display: flex;
                justify-content: flex-end;
                gap: 10px;
                position: absolute;
                bottom: 0;
                background: #22272d0d;
                width: 100%;
                padding: 5px;
            }
        }
    }
}
</style>
